import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Building2, Microscope, GraduationCap, Cpu, Globe2, BookOpen, Users, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { IMAGES, INDICES } from "@/lib/constants";
import { Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchStats, fetchInsights, fetchCaseStudies } from "@/lib/api";
import SEO from "@/lib/seo";

const heroStatements = [
  { line1: "Buildings are designed to be seen.", line2: "Rarely are they designed to be maintained." },
];

export default function Home() {
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetchInsights({ limit: 3 }).then(setInsights).catch(() => {});
    fetchCaseStudies().then((d) => setCases(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="The Cleanability Institute — Designing the Maintainable Future"
        description="A global think tank, research institute, training academy, and standards body advancing the science of cleanability across the built environment."
        image="https://static.prod-images.emergentagent.com/jobs/3b00bed5-19cb-479a-bb3d-8d0a83985926/images/34ee0cd43d41a3c9480ab9d003ac04e19d6eb93be7fcb1b2aa4aa849fcfa7b8b.png"
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.heroBackground} alt="" className="w-full h-full object-cover opacity-50 dark:opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/55 to-background" />
          <div className="absolute inset-0 grid-pattern opacity-[0.18]" />
        </div>

        <div className="container-x pt-16 md:pt-28 pb-24 md:pb-32 relative">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--accent))] pulse-dot" />
              <span className="eyebrow">A new discipline · Est. 2024</span>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h1 className="mt-8 font-display text-[40px] leading-[1.02] sm:text-6xl lg:text-7xl tracking-tight font-medium text-balance max-w-5xl">
              Buildings are designed to be seen.
              <br />
              <span className="text-muted-foreground">Rarely are they designed to be</span>{" "}
              <span className="font-serif-display italic text-[hsl(var(--accent))]">maintained.</span>
            </h1>
          </Reveal>

          <Reveal delay={0.18}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              The Cleanability Institute is the global think tank, research body, and academy advancing
              the science of how the built environment is cleaned, maintained and lived in across its lifecycle.
            </p>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/science" data-testid="hero-cta-science" className="inline-flex items-center gap-2 px-5 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity">
                Explore the science <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/platform#ci-calculator" data-testid="hero-cta-calculator" className="inline-flex items-center gap-2 px-5 h-12 rounded-md border border-border bg-card text-sm font-medium hover:border-[hsl(var(--accent))] transition-colors">
                Calculate your Cleanability
              </Link>
              <Link to="/membership" data-testid="hero-cta-join" className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-sm font-medium text-foreground/80 hover:text-foreground underline-link">
                Join the Institute <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          {/* stat strip */}
          <Reveal delay={0.4}>
            <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
              {[
                { v: stats?.research_publications ?? 47, l: "Peer-reviewed works" },
                { v: stats?.countries_active ?? 32, l: "Active countries" },
                { v: stats?.partner_institutions ?? 68, l: "Partner institutions" },
                { v: `${stats?.average_ci_uplift ?? 28}%`, l: "Avg. CI uplift" },
              ].map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`} className="bg-card p-6 md:p-8">
                  <div className="font-display text-3xl md:text-5xl font-medium tracking-tight">{s.v}</div>
                  <div className="mt-2 text-xs md:text-sm text-muted-foreground">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT IS CLEANABILITY */}
      <section className="section-y container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionEyebrow testId="section-eyebrow-what">01 · The discipline</SectionEyebrow>
            <Reveal>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                Cleanability is the science of designing spaces that can actually be cared for.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                It sits at the intersection of architecture, facilities management, operational science,
                hygiene, sustainability, materials engineering, and human dignity. It asks a simple,
                unfashionable question: <span className="text-foreground">how will this place be maintained, and at what human and economic cost?</span>
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                For most of the modern era, the answer has been: "we will work it out later." Later
                arrives expensive, unsafe, and quietly unjust. The Institute exists to change that.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="grid grid-cols-2 gap-px bg-border rounded-xl overflow-hidden border border-border mt-8">
                {[
                  ["Architecture", Building2],
                  ["Research", Microscope],
                  ["Academy", GraduationCap],
                  ["Intelligence", Cpu],
                ].map(([label, Icon], i) => (
                  <div key={label} data-testid={`pillar-${i}`} className="bg-card p-5 flex items-center gap-3 hover-lift">
                    <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <SectionEyebrow>02 · Why it matters</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                The hidden costs of designing for the photograph.
              </h2>
              <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
                Across 412 buildings the Institute has studied, an average of 34% of operational labour
                is consumed compensating for choices made on day one of design. Cleanability is not a soft
                concern. It is one of the largest invisible cost centres in the modern built environment.
              </p>
              <Link to="/case-studies" data-testid="home-cta-case-studies" className="mt-8 inline-flex items-center gap-2 text-sm font-medium underline-link">
                See the case studies <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["34%", "Labour absorbed by design friction"],
                  ["£4.2m", "Avg. 15-yr lifecycle saving"],
                  ["28%", "Reduction in chemical use"],
                  ["44%", "Hygiene outcome uplift"],
                ].map(([v, l], i) => (
                  <div key={i} data-testid={`why-stat-${i}`} className="rounded-xl border border-border bg-card p-6 hover-lift">
                    <div className="font-display text-3xl md:text-4xl font-medium tracking-tight text-[hsl(var(--accent))]">{v}</div>
                    <div className="text-xs md:text-sm text-muted-foreground mt-2">{l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INDICES */}
      <section className="section-y container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <SectionEyebrow>03 · The Cleanability Index</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
              A measurable language for the operational future of buildings.
            </h2>
          </div>
          <Link to="/science" data-testid="home-cta-indices" className="text-sm font-medium underline-link inline-flex items-center gap-2">
            All indices <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {INDICES.map((idx, i) => (
            <Reveal key={idx.code} delay={i * 0.06}>
              <div data-testid={`index-card-${idx.code}`} className="rounded-2xl border border-border bg-card p-6 hover-lift relative overflow-hidden h-full">
                <div className="font-mono-mini text-muted-foreground">{idx.code}</div>
                <div className="mt-2 font-display text-xl font-medium tracking-tight">{idx.name}</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{idx.description}</p>
                <div className="mt-6 h-1.5 rounded-full bg-border overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${idx.value}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full bg-[hsl(var(--accent))]"
                  />
                </div>
                <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                  <span>Score</span>
                  <span className="font-display text-foreground font-medium">{idx.value}/100</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PLATFORM PREVIEW */}
      <section className="relative overflow-hidden bg-[#04070e] dark:bg-[#04070e] text-white border-y border-border">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="container-x section-y relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-[hsl(var(--accent))]" />
                <span className="eyebrow text-[hsl(var(--accent))]">04 · CleanabilityIQ</span>
              </div>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">
                An intelligence layer for every maintainable surface on earth.
              </h2>
              <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed">
                CleanabilityIQ unifies CI scoring, building assessment, predictive analytics and operational
                dashboards into a single platform for institutions serious about cleanability at scale.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/platform" data-testid="home-cta-platform" className="inline-flex items-center gap-2 px-5 h-12 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:opacity-90">
                  Explore the platform <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/platform#demo" data-testid="home-cta-demo" className="inline-flex items-center gap-2 px-5 h-12 rounded-md border border-white/15 text-sm font-medium text-white/90 hover:bg-white/5">
                  Request a demo
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <div className="relative rounded-2xl border border-white/10 overflow-hidden float-y">
                  <img src={IMAGES.dashboard} alt="CleanabilityIQ" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-md bg-black/50 backdrop-blur px-3 py-1.5 text-xs">
                    <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] pulse-dot relative" />
                    Live · 412 buildings monitored
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section-y container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <SectionEyebrow>05 · Sectors</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
              Every sector has its own cleanability physics.
            </h2>
          </div>
          <Link to="/sectors" data-testid="home-cta-sectors" className="text-sm font-medium underline-link inline-flex items-center gap-2">
            All sectors <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {["Healthcare", "Education", "Hospitality", "Airports", "Public Transport", "Manufacturing", "Laboratories", "Public Infrastructure"].map((s, i) => (
            <Link to="/sectors" key={s} data-testid={`home-sector-${i}`} className="bg-card p-6 md:p-8 hover-lift group">
              <div className="font-mono-mini text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-4 font-display text-lg font-medium tracking-tight">{s}</div>
              <ArrowUpRight className="mt-6 h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--accent))] transition-colors" />
            </Link>
          ))}
        </div>
      </section>

      {/* GLOBAL MAP TEASER */}
      <section className="border-y border-border bg-secondary/40">
        <div className="container-x section-y">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <SectionEyebrow>06 · Global</SectionEyebrow>
              <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium text-balance">A discipline being formed in 32 countries.</h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Pilot projects, research partnerships, training cohorts and standards working groups are
                forming on every continent. Explore where the discipline is taking root.
              </p>
              <Link to="/map" data-testid="home-cta-map" className="mt-8 inline-flex items-center gap-2 text-sm font-medium underline-link">
                Open the global map <Globe2 className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div className="relative rounded-2xl overflow-hidden border border-border">
                <img src={IMAGES.globalMap} alt="Global map" className="w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="section-y container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <SectionEyebrow>07 · Insights</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">From the Institute.</h2>
          </div>
          <Link to="/insights" data-testid="home-cta-insights" className="text-sm font-medium underline-link inline-flex items-center gap-2">
            All writing <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {insights.map((post, i) => (
            <Reveal key={post.id} delay={i * 0.08}>
              <Link
                to={`/insights/${post.slug}`}
                data-testid={`home-insight-${i}`}
                className="block rounded-2xl border border-border bg-card p-6 md:p-8 hover-lift h-full group"
              >
                <div className="font-mono-mini text-muted-foreground">{post.category} · {post.read_minutes} min</div>
                <div className="mt-4 font-display text-xl md:text-2xl font-medium tracking-tight text-balance">{post.title}</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
                <div className="mt-8 flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">{post.author}</div>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--accent))] transition-colors" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CASE STUDIES TEASER */}
      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionEyebrow>08 · Case studies</SectionEyebrow>
          <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight font-medium max-w-3xl text-balance">
            What happens when cleanability is taken seriously.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {cases.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <div data-testid={`home-case-${i}`} className="rounded-2xl border border-border bg-card p-6 md:p-8 h-full">
                  <div className="font-mono-mini text-muted-foreground">{c.sector} · {c.location}</div>
                  <div className="mt-4 font-display text-lg md:text-xl font-medium tracking-tight">{c.title}</div>
                  <div className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground">CI before</div>
                      <div className="font-display text-2xl font-medium">{c.ci_before}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">CI after</div>
                      <div className="font-display text-2xl font-medium text-[hsl(var(--accent))]">{c.ci_after}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER QUOTE */}
      <section className="border-y border-border bg-secondary/30">
        <div className="container-x section-y">
          <Reveal>
            <Quote className="h-8 w-8 text-[hsl(var(--accent))]" />
            <blockquote className="mt-6 font-serif-display text-2xl md:text-4xl leading-snug tracking-tight max-w-4xl text-balance">
              "Civilisation is held together not by what is admired, but by what is maintained.
              Cleanability is the unfinished sentence in modern architecture."
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-[hsl(var(--accent))]/15 grid place-items-center text-[hsl(var(--accent))] font-display font-medium">FD</div>
              <div>
                <div className="font-display font-medium">Fuseini Dawuda</div>
                <div className="text-xs text-muted-foreground">Founder · The Cleanability Institute</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* JOIN CTA */}
      <section className="container-x section-y">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 lg:p-20 relative overflow-hidden">
          <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-[hsl(var(--accent))]/10 blur-3xl" />
          <div className="relative max-w-3xl">
            <SectionEyebrow>09 · Membership</SectionEyebrow>
            <h2 className="mt-6 font-display text-3xl md:text-5xl lg:text-6xl tracking-tight font-medium text-balance">
              Help write the discipline that didn't exist yesterday.
            </h2>
            <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed">
              Researchers, practitioners, designers, operators and institutions are convening at the Institute.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/membership" data-testid="home-cta-membership" className="inline-flex items-center gap-2 px-5 h-12 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                Become a member <Users className="h-4 w-4" />
              </Link>
              <Link to="/research" data-testid="home-cta-research" className="inline-flex items-center gap-2 px-5 h-12 rounded-md border border-border text-sm font-medium hover:border-[hsl(var(--accent))]">
                Read the research <BookOpen className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
